import { useState, useEffect, useRef, createContext, useContext } from 'react';
import { Menu, X, Globe, ChevronDown, Phone, Mail, MapPin } from 'lucide-react';
import { COMPANY_INFO, NAV_LINKS } from '@/lib/index';
import { motion, AnimatePresence } from 'framer-motion';


/* ─── Language Context ─── */
export type Lang = 'en' | 'ar';
export const LangContext = createContext<{ lang: Lang; setLang: (l: Lang) => void }>({
  lang: 'en',
  setLang: () => {},
});
export const useLang = () => useContext(LangContext);

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [lang, setLang] = useState<Lang>('en');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLElement>(null);
  const dir = lang === 'ar' ? 'rtl' : 'ltr';
// 1. أضف useRef و useState في بداية المكون
const [dropdownOpen, setDropdownOpen] = useState(false);
const dropdownRef = useRef<HTMLDivElement>(null);

// 2. أضف هذا الـ useEffect لإغلاق القائمة عند النقر في أي مكان آخر بالخارج
useEffect(() => {
  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setDropdownOpen(false);
    }
  };
  document.addEventListener('mousedown', handleClickOutside);
  return () => document.removeEventListener('mousedown', handleClickOutside);
}, []);
  useEffect(() => {
    document.body.className = lang === 'ar' ? 'lang-ar' : 'lang-en';
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
  }, [lang, dir]);

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const h = headerRef.current.offsetHeight;
        setHeaderHeight(h);
      }
    };
    updateHeight();
    const ro = new ResizeObserver(updateHeight);
    if (headerRef.current) ro.observe(headerRef.current);
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => { ro.disconnect(); window.removeEventListener('scroll', onScroll); };
  }, []);

  const scrollTo = (href: string) => {
    setMobileMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <LangContext.Provider value={{ lang, setLang }}>
      <div className="min-h-screen flex flex-col" dir={dir}>

        {/* ── Top bar ── */}
        <div className="hidden md:block text-xs py-2 border-b border-border" style={{ background: '#090C09', color: 'rgba(249,244,240,0.65)' }}>
          <div className="container mx-auto px-6 flex items-center justify-between">
            <div className="flex items-center gap-5">
              <a href={`tel:${COMPANY_INFO.contact.phone}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
                <Phone className="w-3 h-3" style={{ color: '#73CB60' }} />
                {COMPANY_INFO.contact.phone}
              </a>
              <a href={`mailto:${COMPANY_INFO.contact.email}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
                <Mail className="w-3 h-3" style={{ color: '#73CB60' }} />
                {COMPANY_INFO.contact.email}
              </a>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5">
                <MapPin className="w-3 h-3" style={{ color: '#73CB60' }} />
                {lang === 'ar' ? 'المرج، ليبيا' : 'Al Marj, Libya'}
              </span>
            </div>
          </div>
        </div>

        {/* ── Header ── */}
        <header
          ref={headerRef}
          className={`fixed z-50 left-0 right-0 transition-all duration-300 ${
            scrolled
              ? 'bg-white shadow-md border-b border-border'
              : 'bg-white border-b border-border/50'
          }`}
          style={{ top: 0 }}
        >
          <div className="container mx-auto px-6">
            <div className="flex items-center justify-between h-20">

              {/* Logo */}
              <a
                href="#hero"
                onClick={e => { e.preventDefault(); scrollTo('#hero'); }}
                className="flex items-center gap-3 flex-shrink-0"
              >
                <div className="w-12 h-12 flex-shrink-0">
                  <img src="/images/Screenshot9609.png" alt="Al-Qutoof Logo" className="w-full h-full object-contain" />
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="font-black text-base" style={{ color: '#090C09' }}>
                    {lang === 'ar' ? 'القطوف الخضراء' : 'Al-Qutoof Al-Qhadraa'}
                  </span>
                  <span className="text-xs font-medium" style={{ color: '#73CB60' }}>
                    {lang === 'ar' ? 'استيراد الآلات الزراعية' : 'Agri-Machinery & Spare Parts'}
                  </span>
                </div>
              </a>

              {/* Desktop Nav */}
              <nav className="hidden lg:flex items-center gap-1">
                {NAV_LINKS.map(link => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={e => { e.preventDefault(); scrollTo(link.href); }}
                    className="px-4 py-2 text-sm font-semibold transition-all rounded hover:text-primary"
                    style={{ color: '#2A292F' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#73CB60'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#2A292F'; }}
                  >
                    {lang === 'ar' ? link.labelAr : link.labelEn}
                  </a>
                ))}
              </nav>
{/* Language Switcher */}




              {/* Right controls */}
              <div className="flex items-center gap-3 ">
                {/* Language Switcher */}
                <div className="relative z-[60]" ref={dropdownRef}>
  <button
    onClick={() => setDropdownOpen(!dropdownOpen)} // يفتح ويغلق عند النقر
    onMouseEnter={() => setDropdownOpen(true)}      // يفتح عند التحويم (Desktop)
    className="flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border border-border rounded hover:border-primary transition-colors bg-white"
    style={{ color: '#2A292F' }}
  >
    <Globe className="w-4 h-4" style={{ color: '#73CB60' }} />
    {lang === 'ar' ? 'العربية' : 'English'}
    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} />
  </button>

  {/* القائمة المنسدلة */}
  <AnimatePresence>
    {dropdownOpen && (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 10 }}
        onMouseLeave={() => setDropdownOpen(false)} // يغلق عند خروج الماوس
        className="absolute top-full mt-1 bg-white border border-border rounded shadow-lg overflow-hidden z-[70] min-w-[110px]"
        style={{ [dir === 'rtl' ? 'left' : 'right']: 0 }}
      >
        <button
          onClick={() => { setLang('en'); setDropdownOpen(false); }}
          className={`w-full px-4 py-2.5 text-sm font-semibold text-start hover:bg-secondary transition-colors ${lang === 'en' ? 'text-primary' : 'text-foreground'}`}
        >
          English
        </button>
        <button
          onClick={() => { setLang('ar'); setDropdownOpen(false); }}
          className={`w-full px-4 py-2.5 text-sm font-semibold text-start hover:bg-secondary transition-colors ${lang === 'ar' ? 'text-primary' : 'text-foreground'}`}
        >
          العربية
        </button>
      </motion.div>
    )}
  </AnimatePresence>
</div>
                {/* CTA */}
                <a
                  href="#contact"
                  onClick={e => { e.preventDefault(); scrollTo('#contact'); }}
                  className="btn-orange hidden md:inline-flex"
                >
                  {lang === 'ar' ? 'اتصل بنا' : 'Contact Us'}
                </a>

                {/* Mobile menu button */}
                <button
                  className="lg:hidden p-2 rounded hover:bg-secondary transition-colors"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  aria-label="Menu"
                >
                  {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Menu */}
          <AnimatePresence>
            {mobileMenuOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="lg:hidden bg-white border-t border-border"
              >
                <nav className="container mx-auto px-6 py-4 flex flex-col gap-1">
                  {NAV_LINKS.map(link => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={e => { e.preventDefault(); scrollTo(link.href); }}
                      className="px-3 py-3 text-base font-semibold text-foreground hover:text-primary border-b border-border/50 transition-colors"
                    >
                      {lang === 'ar' ? link.labelAr : link.labelEn}
                    </a>
                  ))}
                  <a href="#contact" onClick={e => { e.preventDefault(); scrollTo('#contact'); }} className="btn-orange mt-3 justify-center">
                    {lang === 'ar' ? 'اتصل بنا' : 'Contact Us'}
                  </a>
                </nav>
              </motion.div>
            )}
          </AnimatePresence>
        </header>

        {/* Main content */}
        <main style={{ paddingTop: `${headerHeight}px` }} className="flex-1">
          {children}
        </main>

        {/* ── Footer ── */}
        <footer style={{ background: '#090C09' }}>
          {/* Top footer */}
          <div className="container mx-auto px-6 py-16">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-10">

              {/* Brand */}
              <div className="md:col-span-1">
                <div className="flex items-center gap-3 mb-5">
                  <img src="/images/Screenshot9609.png" alt="Logo" className="w-12 h-12 object-contain" />
                  <div>
                    <p className="font-black text-base" style={{ color: '#F9F4F0' }}>
                      {lang === 'ar' ? 'القطوف الخضراء' : 'Al-Qutoof Al-Qhadraa'}
                    </p>
                    <p className="text-xs" style={{ color: '#73CB60' }}>
                      {lang === 'ar' ? 'استيراد الآلات الزراعية' : 'Agri-Machinery Importers'}
                    </p>
                  </div>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(249,244,240,0.5)' }}>
                  {lang === 'ar'
                    ? 'رائدون في استيراد الآلات وقطع غيار المعدات الزراعية في ليبيا منذ الثمانينيات.'
                    : 'Leading importers of agricultural machinery and spare parts in Libya since the 1980s.'}
                </p>
              </div>

              {/* Quick Links */}
              <div>
                <h4 className="font-black text-sm uppercase tracking-widest mb-5" style={{ color: '#73CB60' }}>
                  {lang === 'ar' ? 'روابط سريعة' : 'Quick Links'}
                </h4>
                <nav className="flex flex-col gap-2.5">
                  {NAV_LINKS.map(link => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={e => { e.preventDefault(); scrollTo(link.href); }}
                      className="text-sm transition-colors hover:text-white"
                      style={{ color: 'rgba(249,244,240,0.5)' }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#fff'; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(249,244,240,0.5)'; }}
                    >
                      {lang === 'ar' ? link.labelAr : link.labelEn}
                    </a>
                  ))}
                </nav>
              </div>

              {/* Products */}
              <div>
                <h4 className="font-black text-sm uppercase tracking-widest mb-5" style={{ color: '#73CB60' }}>
                  {lang === 'ar' ? 'منتجاتنا' : 'Our Products'}
                </h4>
                <div className="flex flex-col gap-2.5">
                  {['JOHN DEERE', 'CLAAS', 'NEW HOLLAND', 'ROSTSELMASH', 'Özduman'].map(b => (
                    <span key={b} className="text-sm font-mono" style={{ color: 'rgba(249,244,240,0.5)' }}>{b}</span>
                  ))}
                </div>
              </div>

              {/* Contact */}
              <div>
                <h4 className="font-black text-sm uppercase tracking-widest mb-5" style={{ color: '#73CB60' }}>
                  {lang === 'ar' ? 'تواصل معنا' : 'Contact'}
                </h4>
                <div className="flex flex-col gap-3">
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#73CB60' }} />
                    <div>
                      <p className="text-sm" style={{ color: 'rgba(249,244,240,0.7)' }}>
                        {lang === 'ar' ? 'طرابلس - النوفليين' : 'Tripoli – Al-Nofleen'}
                      </p>
                      <p className="text-sm" style={{ color: 'rgba(249,244,240,0.5)' }}>
                        {lang === 'ar' ? 'المرج - الطريق الساحلي' : 'Al Marj – Coastal Road'}
                      </p>
                    </div>
                  </div>
                  <a href={`tel:${COMPANY_INFO.contact.phone}`} className="flex items-center gap-2 text-sm transition-colors hover:text-white" style={{ color: 'rgba(249,244,240,0.7)' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#fff'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(249,244,240,0.7)'; }}>
                    <Phone className="w-4 h-4 flex-shrink-0" style={{ color: '#73CB60' }} />
                    {COMPANY_INFO.contact.phone}
                  </a>
                  <a href={`mailto:${COMPANY_INFO.contact.email}`} className="flex items-center gap-2 text-sm break-all transition-colors hover:text-white" style={{ color: 'rgba(249,244,240,0.7)' }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#fff'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(249,244,240,0.7)'; }}>
                    <Mail className="w-4 h-4 flex-shrink-0" style={{ color: '#73CB60' }} />
                    {COMPANY_INFO.contact.email}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t" style={{ borderColor: 'rgba(249,244,240,0.1)' }}>
            <div className="container mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
              <p className="text-xs" style={{ color: 'rgba(249,244,240,0.35)' }}>
                © 2026 Al-Qutoof Al-Qhadraa.{' '}
                {lang === 'ar' ? 'جميع الحقوق محفوظة.' : 'All rights reserved.'}
              </p>
              <p className="text-xs font-mono" style={{ color: '#73CB60', opacity: 0.6 }}>
                LIBYA · SINCE 1980s
              </p>
            </div>
          </div>
        </footer>
      </div>
    </LangContext.Provider>
  );
}
